/** @jsx jsx */
import './edit-model.css'
import { css, jsx } from '@emotion/react';
import styles from './edit-model.styles';
import SharedStyles from '../../_shared/styles';
import { ChangeEvent, FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { cloneDeep, find, get, isEqual, set, uniq, size } from 'lodash';
import Link from '../../_shared/link';
import { Button } from '../../_shared/button';
// AER-1648: import all renderable AntD controls from the `antd` barrel, never
// the deep `antd/lib/...` (CommonJS) paths. config-overrides.js rewrites barrel
// imports to `antd/es/*` via babel-plugin-import, so the app's ConfigProvider
// (also from the barrel) and these controls share ONE config-provider React
// context. Deep `antd/lib/*` imports load the separate CJS module graph with its
// own ConfigProvider context, which the es/ provider never populates -> those
// controls fell back to AntD6's default theme (blue focus, 6px radius). Type-only
// imports (SelectValue, RcFile) never render, so a deep path is harmless there.
import { Alert, App, AutoComplete, Form, Input, Modal, Select, Switch, Upload, message } from 'antd';
import { AppState } from '../../app.state';
import { SelectValue } from 'antd/lib/select';
import { TableTitleHeader } from '../../_shared/table-list';
import { Opt } from '../../_shared/interfaces/opt';
import { Model, Threshold, Thresh, EquipmentType, DefaultInfoRows, modelHasBoardVersions } from '../../_shared/interfaces/model';
import { User } from '../../_shared/interfaces/user';
import { getModel, getModels, syncModelThresholds, updateModel, uploadModelImage } from '../../_shared/services/manage-models.service';
import { EditableFormTable } from '../../_shared/editable-table';
import { ensureArray, getSensorsForEquipmentType, sortByKey, uppercaseWords } from '../../utils';
import { RcFile } from 'antd/lib/upload';
import getConfig, { isProd } from '../../config'
import { InstructionsPage } from '../../_shared/interfaces/model';
import colors from '../../_shared/colors';
import { EditInstructions } from './edit-instructions';
import ModelImage from '../../_shared/model-image';
import { InputTag } from '../../_shared/input-tag';
import { AIcon as Icon } from '../../_shared/icon';
import { WithForm } from '../../_shared/form';
import { SN_TYPE_CODE_ANY, defaultSnTypeCodes, normalizeSnTypeCodes } from '../../_shared/lib/sn-type-codes';

type DataSourceItemType = any;

interface EquipmentType_option {
  name: EquipmentType;
  displayName: string;
  defaultThresholds: Thresh;
};

const validateVersion = (rule: any, value: string[], cb: any) => {
  const regex = /^(\d+\.)?(\d+\.)?(\*|\d+)$/;
  const failed = ensureArray(value).find(v => !regex.test(v));
  cb(!value || !failed ? undefined : rule.message);
};

const validateMax = (rule: any, value: string[], cb: any) => {
  const max = 8;
  const failed = ensureArray(value).find(v => size(v) > max);
  cb(!value || !failed ? undefined : rule.message);
};

const versionRules = [
  // AER-1650: Control Board HW/FW and Daughterboard FW must NOT be required.
  // They are frequently left empty, and a `required` rule kept the form in an
  // error state so Save stayed disabled and updates could never be saved. The
  // remaining validators short-circuit on empty values, so they only apply
  // once the user actually enters a tag.
  {
    validator: validateMax,
    message: 'Value is limited to 8 characters. ',
  },
  {
    validator: validateVersion,
    message: 'Value is not valid. ',
  },
];

// AER-1795: the serial type codes Manufacturing and Edit Equipment accept for this model.
// Codes are 1-3 letters/digits (CR, CS, BN, QT, AH8, ABU, 00); '*' accepts any serial.
const validateSnTypeCodes = async (_rule: unknown, value: string[]) => {
  const codes = normalizeSnTypeCodes(value);
  if (!codes.length) {
    throw new Error('Enter at least one serial number type code, or * to accept any serial.');
  }
  const invalid = codes.find(c => c !== SN_TYPE_CODE_ANY && !/^[A-Z0-9]{1,3}$/.test(c));
  if (invalid) {
    throw new Error(`"${invalid}" is not a valid type code. Use 1-3 letters or digits, or * to accept any serial.`);
  }
};

// Pre-fills the codes of a model that has none yet (a new model, or one saved before
// AER-1795) with the default for its type, which is what the server would store anyway.
const withDefaultSnTypeCodes = (model: Model): Model =>
  normalizeSnTypeCodes(model.snTypeCodes).length
    ? model
    : { ...model, snTypeCodes: defaultSnTypeCodes(model) };

const sharedDefaultThresholds: Thresh = {
  AH: {
    minorUpper: 95,
  },
  AT: {
    minorLower: 32,
    minorUpper: 120,
  },
  BP: {
    minorLower: 90,
    majorLower: 20,
  },
};

const defaultInstruction = (eqType: EquipmentType): InstructionsPage[] =>
  (eqType === "aerasone_compressor") ? [
    {
      title: 'Compressor Step 1: Install Current Clamp',
      body: '',
      images: []
    },
    {
      title: 'Compressor Step 2: Connect to Remote Indicator',
      body: '',
      images: []
    },
    {
      title: 'Compressor Step 3: Install Pressure Sensor',
      body: '',
      images: []
    },
    {
      title: 'Compressor Step 4: Attach Accelerometer',
      body: '',
      images: []
    }
  ] : (eqType === "aerasone_vacuum") ? [
    {
      title: 'Vacuum Step 1: Install Current Clamp',
      body: '',
      images: []
    },
    {
      title: 'Vacuum Step 2: Connect to Remote Indicator',
      body: '',
      images: []
    },
    {
      title: 'Vacuum Step 3: Install & Calibrate Suction Sensor',
      body: '',
      images: []
    },
    {
      title: 'Vacuum Step 4: Attach Accelerometer',
      body: '',
      images: []
    }
  ] : [
  ];
;

const equipmentTypes = [
  {
    name: "aerasone_compressor",
    displayName: "Aeras One - Compressor",
    defaultThresholds: sharedDefaultThresholds,
  },
  {
    name: "aerasone_vacuum",
    displayName: "Aeras One - Vacuum",
    defaultThresholds: sharedDefaultThresholds,
  },
  {
    name: "compressor",
    displayName: "Compressor",
    defaultThresholds: [],
  },
  {
    name: "vacuum",
    displayName: "Vacuum",
    defaultThresholds: [],
  },
  {
    name: "chair",
    displayName: "Chair",
    supportedSignals: [],
    defaultThresholds: [],
  },
  {
    name: "handpiece",
    displayName: "Handpiece",
    supportedSignals: [],
    defaultThresholds: [],
  },
  {
    name: "sterilizer",
    displayName: "Sterilizer",
    supportedSignals: [],
    defaultThresholds: [],
  },
] as EquipmentType_option[];

const thresholdColumns = [
  {
    title: 'Sensor ID',
    dataIndex: 'key',
  },
  {
    title: 'Sensor',
    dataIndex: 'sensorName',
  },
  {
    title: 'Units',
    dataIndex: 'suffix',
  },
  {
    title: 'Minor Lower',
    dataIndex: 'minorLower',
    editable: true,
  },
  {
    title: 'Major Lower',
    dataIndex: 'majorLower',
    editable: true,
  },
  {
    title: 'Minor Upper',
    dataIndex: 'minorUpper',
    editable: true,
  },
  {
    title: 'Major Upper',
    dataIndex: 'majorUpper',
    editable: true,
  },
  {
    title: 'Duration (Lower)',
    dataIndex: 'durationLower',
    editable: true,
  },
  {
    title: 'Duration (Upper)',
    dataIndex: 'durationUpper',
    editable: true,
  }
];

const modelIdDoesntExist = async (rule: any, value: string) => {
  let model = await getModel(value);
  if (model) {
    throw new Error(`Model already exists with ID ${value}`);
  }
}

interface IProps {
  sState: AppState;
  model?: Model;
  backText: string;
  editingModel: boolean;
  goToPage?: number;
};

type InputType = "dropdown" | "text" | "autocomplete" | "textarea" | "image" | "boolean" | "input-tag";
interface TDisplay {
  title?: string;
  extra?: ReactNode;
  placeholder?: string;
  var: string;
  canDisplay?: () => boolean;
  transform?: (value: string) => string;
  options?: any;
  data?: () => string[];
  opts?: Opt[];
  type: InputType;
  value?: string;
  label?: string;
  disabled?: boolean;
  suffixIcon?: ReactNode;
  completions?: DataSourceItemType[];
  rows?: number;
  className?: string;
};

function validHeads(equipmentType: string): number[] {
  switch (equipmentType) {
    case "aerasone_compressor":
    case "compressor":
      return [1, 2, 3];
    case "aerasone_vacuum":
    case "vacuum":
    case "handpiece":
      return [1];
    default:
      return [0];
  }
}

const EditModelImage: React.FC<{
  model: Model,
  imagePath: string,
  onChange: (filename?: string) => void,
  form?: any,
}> = props => {

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const onRemove = () => {
    onChange(undefined);
  };

  const { model, imagePath, onChange } = props;

  const upload = async (file: RcFile) => {
    setError('');
    let result = await uploadModelImage(file);
    // hacky - to implement custom upload, antd docs suggest
    // using beforeUpload and returning false or a rejected Promise
    // from it to prevent the "action" from being called. leaving
    // "action" undefined just results in the dragger posting the
    // file to the current URL. so we either throw a bogus error
    // here, or hit a 404.
    if (typeof (result) === 'string' || !result.data.success) {
      const error = (typeof (result) === 'string') ? result : result.data.error;
      setError(error || 'upload failed');
      throw Error(`Upload failed: ${error}`);
    } else {
      onChange(result.data.filename);
      throw Error(`Upload succeeded`);
    }
  };

  const image = get(model, imagePath, undefined) as string;
  if (image) {
    const fullPath = `${getConfig().model_photos}${image}`;
    return (
      <div
        className={'image-container'}
      >
        <img src={fullPath} alt={'Model'} onLoad={(() => { setLoading(false); })} />
        <Icon className='loading-icon' type="loading" style={{ opacity: loading ? 1 : 0 }} />
        <div onClick={onRemove} className='delete-button'>
          <Icon
            className='delete-icon'
            onClick={onRemove}
            style={{ fontSize: '24px', color: colors.white, backgroundColor: colors.highlight }}
            type='delete'
          />
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <Upload.Dragger
          name='image'
          multiple={false}
          showUploadList={false}
          accept=".png,.jpg,.jpeg,image/jpeg,image/png,image/jpg"
          beforeUpload={upload}
        >
          <p className="ant-upload-drag-icon">
            {/* AER-1648: AntD6's Dragger theme forces the inbox glyph to the
                primary blue via a layered CSS-in-JS rule on `.anticon`; an inline
                color did not stick. Override with an Emotion rule (unlayered, so it
                wins the cascade) that targets the nested svg/path fill directly.
                `&&` doubles specificity to beat AntD's selector; scoped to this icon. */}
            <Icon
              type="inbox"
              css={css`
                &&,
                && svg,
                && svg path {
                  color: ${colors.highlight};
                  fill: ${colors.highlight};
                }
              `}
            />
          </p>
          <p className="ant-upload-text">
            Click or drag image to this area to upload
          </p>
          <p className="ant-upload-error">
            {error}
          </p>
        </Upload.Dragger>
      </div>
    );
  }
};

interface EditModelComponentProps {
  sState: AppState;
}

const _EditModelComponent: React.FC<
  RouteComponentProps & { form?: any } & EditModelComponentProps
> = props => {

  const { modal } = App.useApp();

  const emptyModel: Model = {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    id: '',
    displayName: '',
    isActive: 1,
    type: 'compressor',
    family: '',
    manufacturer: '',
    name: '',
    image: '',
    images: {},
    heads: 1,
    thresholds: {},
    description: '',
    infoRows: DefaultInfoRows('compressor'),
  };

  const { form, sState } = props;

  const {
    model,
    backText,
    editingModel,
    goToPage,
  } = props.location.state as IProps;

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [workingModel, setWorkingModel] = useState<Model>(() => withDefaultSnTypeCodes(model || emptyModel));
  const [models, setModels] = useState<Model[]>([]);
  const [editingThresholds, setEditingThresholds] = useState<boolean>(false);
  const [thresholdsEdited, setThresholdsEdited] = useState<boolean>(false);
  const [showEditInstructions, setShowEditInstructionsModal] = useState<boolean>(goToPage !== undefined);

  // hard-coded for four-page instructions for now

  const isAerasOne = useMemo(
    () => {
      return workingModel ? workingModel.type.includes('aerasone') : false;
    }, [workingModel]
  );

  if (isAerasOne && (!workingModel.instructions || workingModel.instructions.length !== 4)) {
    setWorkingModel({ ...workingModel, instructions: defaultInstruction(workingModel.type) });
  }

  const showVersions = useMemo(
    () => {
      // AER-1713: 5400 and CRK carry the same control-board values as the 6400,
      // so they must be editable on all three models.
      return modelHasBoardVersions(workingModel);
    }, [workingModel]
  );

  const manufacturers = useMemo(
    () => {
      const allMfrs = models.map(m => m.manufacturer || '');
      return uniq(allMfrs).filter(m => m.length > 0);
    },
    [models]
  );

  const families = useMemo(
    () => {
      const allFamilies = models.map(m => m.family || '');
      return uniq(allFamilies).filter(m => m.length > 0);
    },
    [models]
  );

  const formData: TDisplay[] = useMemo(() => {
    return [
      {
        title: 'Model ID',
        var: 'id',
        type: 'text',
        disabled: editingModel,
        options: {
          initialValue: workingModel.id || '',
          validateTrigger: 'onBlur',
          rules: [
            {
              required: true,
              message: 'Model ID is required',
            },
            {
              max: 15,
              message: 'Model ID is limited to 15 characters.',
            },
            ...editingModel ? [] : [{
              validator: modelIdDoesntExist,
              message: 'This model ID is already in use - please enter a new model ID.',
            }]
          ],
        },
      },
      {
        title: 'Display Name/ID',
        var: 'displayName',
        type: 'text',
        options: {
          initialValue: workingModel.displayName || '',
          validateTrigger: 'onBlur',
          rules: [
            {
              required: true,
              message: 'Display Name/ID is required',
            }
          ]
        },
      },
      {
        title: 'Equipment Type',
        var: 'type',
        type: 'dropdown',
        opts: equipmentTypes.map(t => ({ label: t.displayName, value: t.name })),
        options: {
          initialValue: workingModel.type,
          validateTrigger: 'onBlur',
          rules: [
            {
              required: true,
              message: 'Equipment type is required',
            },
          ],
        },
      },
      {
        title: 'Model Name',
        var: 'name',
        type: 'text',
        options: {
          initialValue: workingModel.name,
          validateTrigger: 'onBlur',
          rules: [
            {
              required: true,
              message: 'Model name is required',
            },
            {
              max: 75,
              message: 'Name is limited to 75 characters.',
            }
          ],
        },
      },
      {
        title: 'Manufacturer',
        var: 'manufacturer',
        type: 'autocomplete',
        options: {
          initialValue: workingModel.manufacturer,
          validateTrigger: 'onBlur',
          rules: [
            {
              required: true,
              message: 'Manufacturer is required.',
            },
            {
              max: 30,
              message: 'Manufacturer is limited to 30 characters.',
            },
            {
              min: 1,
              message: 'Manufacturer is required.',
            }
          ]
        },
        completions: manufacturers,
        transform: uppercaseWords,
      },
      {
        title: 'Family',
        var: 'family',
        type: 'autocomplete',
        options: {
          initialValue: workingModel.family,
          validateTrigger: 'onBlur',
          rules: [
            {
              max: 30,
              message: 'Family is limited to 30 characters.',
            },
          ]
        },
        completions: families,
        transform: uppercaseWords,
      },
      {
        title: 'Heads',
        var: 'heads',
        type: 'dropdown',
        opts: validHeads(workingModel.type).map(h => ({ value: h, label: h })),
        canDisplay: () => { return validHeads(workingModel.type).length > 1; },
        options: {
          initialValue: workingModel.heads,
          validateTrigger: 'onBlur',
          rules: [
            {
              required: workingModel.type === 'aerasone_compressor',
              message: '# of heads is required for compressors',
            },
          ],
        },
      },
      {
        title: 'Description',
        var: 'description',
        type: 'textarea',
        className: 'form-item-wide',
        options: {
          initialValue: workingModel.description,
          rules: [
            {
              max: 500,
              message: 'Description is limited to 500 characters.',
            }
          ]
        },
        rows: 3,
      },
      {
        title: 'Control Board Hardware',
        var: 'control_board_hw',
        type: 'input-tag',
        canDisplay: () => showVersions,
        options: {
          initialValue: workingModel.control_board_hw,
          validateTrigger: 'onChange',
          rules: versionRules,
        },
      },
      {
        title: 'Control Board Firmware',
        var: 'control_board_fw',
        type: 'input-tag',
        canDisplay: () => showVersions,
        options: {
          initialValue: workingModel.control_board_fw,
          validateTrigger: 'onChange',
          rules: versionRules,
        },
      },
      {
        title: 'Daughterboard Firmware',
        var: 'fw_version',
        type: 'input-tag',
        canDisplay: () => showVersions,
        options: {
          initialValue: workingModel.fw_version,
          validateTrigger: 'onChange',
          rules: versionRules
        },
      },
      {
        title: 'Serial Number Type Codes',
        var: 'snTypeCodes',
        type: 'input-tag',
        placeholder: 'Add a type code...',
        extra: `Serial numbers for this model must carry one of these codes. ${SN_TYPE_CODE_ANY} accepts any serial.`,
        options: {
          initialValue: workingModel.snTypeCodes,
          validateTrigger: 'onTagsChange',
          rules: [{ validator: validateSnTypeCodes }],
        },
      },
      {
        title: 'Show on Manufacturing',
        var: 'showOnManu',
        type: 'boolean',
        canDisplay: () => !isAerasOne,
        options: {
          initialValue: !!workingModel.showOnManu,
        },
      },
      {
        // AER-1687: when off, the model is hidden from customer-facing dropdowns
        // (dashboard + reporting Model filters). Defaults ON for existing models.
        title: 'Show in Dropdowns',
        var: 'showInDropdowns',
        type: 'boolean',
        options: {
          initialValue: get(workingModel, 'showInDropdowns', true),
        },
      },
      {
        title: 'Model Image',
        var: 'image',
        type: 'image',
      },
    ];
  }, [workingModel, families, manufacturers, editingModel, isAerasOne, showVersions]);

  const goBack = useCallback((force: boolean = false) => {
    const onOk = () => {
      props.history.goBack();
    }
    const onCancel = () => null;

    if (force) {
      onOk();
    } else {
      modal.confirm({
        title: 'Navigation Confirmation',
        content: `Are you sure you want to navigate away from ${editingModel ? 'Edit' : 'Add'
          } Model? The ${editingModel ? '' : 'new '}model will not be ${editingModel ? 'saved' : 'created'
          } and any progress will be lost.`,
        onOk,
        onCancel,
        okText: 'Leave',
      });
    }
  }, [props.history, editingModel, modal]);

  // AER-1667 (R1): the Save button is no longer disabled by field-validation
  // state (the former hasFieldError() gate was removed below); it stays gated
  // only by in-flight saving/loading and the open-threshold-editor guard. Field
  // rules still validate on blur/submit.

  const onChange = useCallback(
    (value: SelectValue | string | number | boolean | undefined, variable: string) => {
      const oldHeads = validHeads(workingModel.type as EquipmentType);
      const oldSnTypeCodeDefault = defaultSnTypeCodes(workingModel);
      if (variable === 'snTypeCodes') {
        value = normalizeSnTypeCodes(value);
      }
      set(workingModel, variable, value);
      form.setFields([{ name: variable, value, errors: [] }]);

      // AER-1795: the non-smart chair and partner chair defaults depend on the model id
      // and family. Follow them unless the codes were changed by hand.
      if (
        (variable === 'id' || variable === 'family') &&
        isEqual(normalizeSnTypeCodes(workingModel.snTypeCodes), oldSnTypeCodeDefault)
      ) {
        workingModel.snTypeCodes = defaultSnTypeCodes(workingModel);
        form.setFieldsValue({ snTypeCodes: workingModel.snTypeCodes });
      }

      if (variable === 'type') {
        // If equipment type has changed, reinitialize dependent parameters with defaults
        if (value === undefined || value === '') {
          workingModel.type = 'compressor';
        } else {
          let et = find(equipmentTypes, et => et.name === value, undefined);
          if (et) {
            // a copy per model: the thresholds grid edits this object in place, which used to
            // change the defaults for every model given this type later in the session
            workingModel.thresholds = cloneDeep(et.defaultThresholds);
            workingModel.instructions = defaultInstruction(value as EquipmentType);
            workingModel.infoRows = DefaultInfoRows(et.name);
          }
        }

        // AER-1795: another type's codes are never right for this one.
        workingModel.snTypeCodes = defaultSnTypeCodes(workingModel);

        const newValidHeads = validHeads(workingModel.type as EquipmentType);
        if (!isEqual(oldHeads, newValidHeads)) {
          workingModel.heads = newValidHeads[0];
        }

        switch (workingModel.type as EquipmentType) {
          case "aerasone_compressor":
          case 'aerasone_vacuum':
          case 'handpiece':
          case 'chair':
            workingModel.showOnManu = false;
            break;
          case 'compressor':
          case 'sterilizer':
          case 'vacuum':
            workingModel.showOnManu = true;
            break;
        }

        // Keep the on-screen controls in sync with the values the equipment-type
        // change just derived (heads list / showOnManu default).
        form.setFieldsValue({
          heads: workingModel.heads,
          showOnManu: !!workingModel.showOnManu,
          snTypeCodes: workingModel.snTypeCodes,
        });
      }

      setWorkingModel({ ...workingModel });
    },
    [workingModel]
  );

  const onSave = useCallback(
    async (e: MouseEvent | FormEvent) => {
      const { validateFields } = form;
      e.preventDefault();
      if (loading || !workingModel) {
        return;
      }

      try {
        setSaving(true);

        let validated = true;
        try {
          await validateFields();
        } catch (err) {
          validated = !size(get(err, 'errorFields', []));
        }

        if (!validated) return;

        // Pull the latest on-screen values into the working model so the save
        // reflects what the user entered (AntD6 binds values through the form).
        const values = form.getFieldsValue();
        Object.keys(values).forEach(k => {
          if (values[k] !== undefined) set(workingModel, k, values[k]);
        });

        formData.forEach(j => {
          const val = get(workingModel, j.var, undefined);
          if (typeof (val) === 'string' && j.transform) {
            set(workingModel, j.var, j.transform(val));
          }
        });

        workingModel.snTypeCodes = normalizeSnTypeCodes(workingModel.snTypeCodes);

        // AER-1692: stamp the audit fields by hand. Nothing else will do it:
        // the server's createItem() stores the item as-is, removeEmpty() (called
        // by createModel) deletes any '' / undefined key before the request
        // leaves the browser, and the axios interceptor's findAllUpdatedAndFix()
        // only REFRESHES an `updatedBy` key that already exists — it never adds
        // one and never touches `createdBy`. Without this block models are
        // written with neither field, and because the keys are absent no later
        // save can ever add them. Mirrors edit-group / edit-location / edit-org.
        const date = new Date().toISOString();
        const currentUser = get(sState, 'auth.user', {}) as User;

        set(workingModel, 'updatedAt', date);
        set(workingModel, 'updatedBy', currentUser.userId);

        if (!editingModel) {
          set(workingModel, 'createdAt', date);
          set(workingModel, 'createdBy', currentUser.userId);
        }

        const result = await updateModel(workingModel);
        // AER-1794: only production pushes thresholds to devices
        if (thresholdsEdited && isProd()) {
          syncModelThresholds(workingModel);
        }
        props.history.replace('/dashboard/manageModel', {
          model: result,
        });
      } catch (err) {
        message.error(err.Message);
      } finally {
        setSaving(false);
      }
    }, [workingModel, loading, props.history, formData, thresholdsEdited, form, sState, editingModel]
  );

  const allSensors = getSensorsForEquipmentType(workingModel.type, sState);

  interface thresholdRow {
    key: string,
    sensorName: string,
    minorLower?: number;
    majorLower?: number;
    minorUpper?: number;
    majorUpper?: number;
    durationLower?: number;
    durationUpper?: number;
    suffix: string;
  };

  const thresholdData: thresholdRow[] = allSensors
    .map(sensor => {
      const sensorId = sensor.id;
      const thresh = get(workingModel.thresholds, sensorId, undefined) as Threshold | undefined;

      const row = {
        key: sensorId,
        sensorName: get(sensor, 'name', ''),
        suffix: get(sensor, 'suffix', ''),
      } as thresholdRow;

      if (thresh) {
        row.majorLower = thresh.majorLower;
        row.minorLower = thresh.minorLower;
        row.minorUpper = thresh.minorUpper;
        row.majorUpper = thresh.majorUpper;
        row.durationLower = thresh.durationLower;
        row.durationUpper = thresh.durationUpper;
      }

      return row;
    }).sort((a, b) => sortByKey(a, b, 'sensorName'));

  const renderForm = useCallback(() => {
    // `WForm` is the migration's shared Form wrapper (bound to this component's
    // AntD6 form instance). We render each field directly via <Form.Item name>
    // so the fields are real children of the `.left-panel`/`.right-panel` CSS
    // grid (AER-1641). Using <Form items> instead nested a separate form per
    // group, which both collapsed the grid to one column and dropped the
    // image/boolean/autocomplete/input-tag controls down to plain text inputs.
    const { Form: WForm } = form;

    const renderFormElement = (item: TDisplay) => {
      const itemOptions = (item.options || {}) as any;
      const canDisplay = item.canDisplay ? item.canDisplay() : true;
      if (!canDisplay) return null;

      const { initialValue, rules, validateTrigger } = itemOptions;

      // The image uploader has no validation/value to bind, so render it inside
      // a label-only Form.Item (no `name`) and drive it from workingModel.
      if (item.type === 'image') {
        return (
          <Form.Item key={item.var} className={item.className} label={item.title}>
            <EditModelImage
              model={workingModel}
              imagePath={item.var}
              onChange={(filename?: string) => { onChange(filename, item.var); }}
            />
          </Form.Item>
        );
      }

      const control =
        item.type === 'dropdown' && item.opts ? (
          <Select
            style={{ width: '100%' }}
            suffixIcon={item.suffixIcon}
            disabled={item.disabled}
            onChange={(value: SelectValue) => onChange(value, item.var)}
          >
            {item.opts.map((opt, i) => (
              <Select.Option key={i} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        ) : (item.type === 'autocomplete') ? (
          <AutoComplete
            onChange={(value: SelectValue) => onChange(value, item.var)}
            placeholder={item.title}
            options={(item.completions || []).map((c: string) => ({ value: c }))}
            filterOption={(inputValue: string, option?: { value?: string }) =>
              ((option && option.value) || '').toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
          />
        ) : (item.type === 'boolean') ? (
          <Switch onChange={(checked: boolean) => onChange(checked, item.var)} />
        ) : (item.type === 'textarea') ? (
          <Input.TextArea
            rows={item.rows || 3}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value, item.var)}
          />
        ) : (item.type === 'input-tag') ? (
          <InputTag
            selected={initialValue}
            placeholder={item.placeholder}
            onTagsChange={(tags?: string[]) => { onChange(tags, item.var); }}
          />
        ) : (
          <Input
            onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value, item.var)}
            disabled={item.disabled}
          />
        );

      return (
        <Form.Item
          key={item.var}
          className={item.className}
          label={item.title}
          extra={item.extra}
          name={item.var}
          initialValue={initialValue}
          rules={rules}
          validateTrigger={validateTrigger}
          valuePropName={item.type === 'boolean' ? 'checked' : undefined}
          {...(item.type === 'input-tag' ? { valuePropName: 'selected', trigger: 'onTagsChange' } : {})}
        >
          {control}
        </Form.Item>
      );
    };

    const onThresholdChanged = async (newData: thresholdRow[], key: string, index: number) => {
      // newData is entire array of thresholdRows passed in
      // key is key of changed row, index is index of changed row in newData
      const newThreshold = newData[index];
      // AER-1703: a cleared <InputNumber /> cell hands back `null`, not '', and the
      // threshold cells are `isRequired: false` so validation lets it through. Keep
      // `null` out of the working model: Threshold types every bound as
      // `number | undefined`, and undefined is what an unset bound has always been.
      const bound = (value?: number | null) => (value === null ? undefined : value);
      set(workingModel, `thresholds.${key}`, {
        minorLower: bound(newThreshold.minorLower),
        minorUpper: bound(newThreshold.minorUpper),
        majorLower: bound(newThreshold.majorLower),
        majorUpper: bound(newThreshold.majorUpper),
        durationLower: bound(newThreshold.durationLower),
        durationUpper: bound(newThreshold.durationUpper),
      });
      setWorkingModel({ ...workingModel });
      setThresholdsEdited(true);
    };

    const renderThresholds = () => {
      if (!isAerasOne) return null;
      // no type signature for EditableFormTable props so we bypass type checking by doing this
      const props = {
        dataSource: thresholdData,
        columns: thresholdColumns,
        loading,
        inputType: 'number',
        isRequired: false,
        onSave: onThresholdChanged,
        setEditing: (isEditing: boolean) => setEditingThresholds(isEditing),
      } as any;
      return (
        <div className='thresholds-panel'>
          <div className='ant-form-item-label' style={{ marginLeft: '5px' }}>Alert Thresholds</div>
          {!isProd() && (
            <Alert
              style={{ marginBottom: 10 }}
              type="warning"
              showIcon
              title="Thresholds are not synced to devices from this environment"
              description="Changes made here are saved to this environment's model only. Devices receive thresholds from the production app."
            />
          )}
          <EditableFormTable id='thresholdstable' {...props} />
        </div>
      );
    };

    const mainFields = formData.filter(item => item.type !== 'image');
    const singleColumnFields = mainFields.filter(item => item.className !== 'form-item-wide');
    const wideFields = mainFields.filter(item => item.className === 'form-item-wide');
    const imageFields = formData.filter(item => item.type === 'image');

    return (
      <WForm layout="vertical" onSubmit={onSave}>
        <div className='panels-grid'>
          <div className='left-panel panel'>
            {imageFields.map(renderFormElement)}
            {(workingModel.images && workingModel.images.chairMount0) && (
              <div>
                <label className='chairmount-label'>Chair Mount</label>
                <ModelImage className='image-container' model={workingModel} imageName='chairMount0' />
              </div>
            )
            }
          </div>
          <div className='right-panel panel'>
            {singleColumnFields.map(renderFormElement)}
            {wideFields.map(renderFormElement)}
            {isAerasOne && <Button
              className='edit-instructions-btn'
              title='Edit Installation Instructions'
              onClick={() => setShowEditInstructionsModal(true)}
            />}
            {isAerasOne && renderThresholds()}
          </div>
        </div>
      </WForm>
    );
  }, [workingModel, form, formData, onChange, onSave, isAerasOne, loading, thresholdData]);

  useEffect(() => {
    const work = async () => {
      setLoading(true);
      setModels(await getModels());
      setLoading(false);
    };
    work();
  }, []);

  const renderEditModel = useCallback(() => {
    if (!workingModel) return null;

    const renderButtons = () => {
      const cancel = (
        <Button
          title={'Cancel'}
          disabled={loading}
          css={css`
            margin: 0 5px;
          `}
          onClick={() => {
            return goBack();
          }}
        />
      );

      const submitButton = (
        <Button
          title={'Save'}
          loading={saving || loading}
          css={css`
            margin: 0 5px;
          `}
          disabled={saving || loading || editingThresholds}
          onClick={onSave}
        />
      );

      return (
        <div css={css(SharedStyles.row, `margin-top: 15px; align-self: center;`)}>
          {cancel}
          {submitButton}
        </div>
      );
    };


    return [
      renderForm(),
      renderButtons(),
    ];
  }, [workingModel, renderForm, editingThresholds, goBack, loading, onSave, saving]);

  const renderEditInstructions = useCallback(() => {
    if (!workingModel || !isAerasOne) return null;
    return <EditInstructions
      instructions={workingModel.instructions || []}
      initialPage={goToPage}
      onCancel={() => {
        if (goToPage !== undefined) {
          goBack(true);
        } else {
          setShowEditInstructionsModal(false);
        }
      }}
      onSave={(newInstructions) => {
        setWorkingModel({ ...workingModel, instructions: newInstructions });
        setShowEditInstructionsModal(false);
      }} />;
  }, [workingModel, goBack, goToPage, isAerasOne]);

  return (
    <div className='edit-model-container' css={css(styles.container)}>
      {!!backText && (
        <Link
          css={css`
            margin-bottom: 5px;
            margin-right: auto;
          `}
          onClick={() => goBack()}
        >
          {backText}
        </Link>
      )}
      <div css={css(SharedStyles.row, styles.rowMargin)}>
        <TableTitleHeader>
          {editingModel ? 'Edit Model' : 'Add Model'}
          {loading && <Icon css={css('margin-left: 5px;')} type="loading" />}
        </TableTitleHeader>
      </div>

      {showEditInstructions ? renderEditInstructions() : renderEditModel()}
    </div>
  );
};

export const EditModelComponent = WithForm(
  withRouter(_EditModelComponent)
);
